import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Satellite } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Projects = () => {
  const { i18n } = useTranslation();

  const title =
    i18n.language?.startsWith("sv")
      ? "Projekt | Publikt finansierade initiativ | Cetasol"
      : i18n.language?.startsWith("ja")
      ? "プロジェクト | 公的資金プロジェクト | Cetasol"
      : i18n.language?.startsWith("zh")
      ? "项目 | 公共资助项目 | Cetasol"
      : "Projects | Publicly Funded Initiatives | Cetasol";

  const description =
    i18n.language?.startsWith("sv")
      ? "En översikt av Cetasols publikt finansierade forsknings- och innovationsprojekt."
      : i18n.language?.startsWith("ja")
      ? "Cetasolが参画する公的資金による研究・イノベーションプロジェクトの一覧。"
      : i18n.language?.startsWith("zh")
      ? "Cetasol 参与的公共资助研究与创新项目概览。"
      : "An overview of Cetasol's publicly funded research and innovation projects.";

  const heading =
    i18n.language?.startsWith("sv")
      ? "Projekt"
      : i18n.language?.startsWith("ja")
      ? "プロジェクト"
      : i18n.language?.startsWith("zh")
      ? "项目"
      : "Projects";

  const intro =
    i18n.language?.startsWith("sv")
      ? "En katalog över våra publikt finansierade projekt — partnerskap som driver forskning, innovation och praktiska resultat för den maritima industrin."
      : i18n.language?.startsWith("ja")
      ? "公的資金による当社のプロジェクト一覧。研究、イノベーション、海事産業の実務的成果を推進するパートナーシップです。"
      : i18n.language?.startsWith("zh")
      ? "我们的公共资助项目目录——推动海事行业研究、创新与实际成果的合作伙伴关系。"
      : "A directory of our publicly funded projects — partnerships that advance research, innovation and practical outcomes for the maritime industry.";

  const readMore =
    i18n.language?.startsWith("sv")
      ? "Läs mer"
      : i18n.language?.startsWith("ja")
      ? "詳しく見る"
      : i18n.language?.startsWith("zh")
      ? "了解更多"
      : "Learn more";

  const projects = [
    {
      icon: Satellite,
      name: "CetaOrbit",
      funder: "European Space Agency (ESA) — ARTES 4.0 Downstream Applications, Programme Line BASS",
      summary:
        i18n.language?.startsWith("sv")
          ? "Ett ESA-stött projekt som integrerar satellitbaserad jordobservation, GNSS och satellitkommunikation med Cetasols iHelm-plattform för smartare resplanering, bränslebesparingar och utsläppsefterlevnad."
          : i18n.language?.startsWith("ja")
          ? "衛星地球観測、GNSS、衛星通信をiHelmプラットフォームに統合するESA支援プロジェクト。航海計画、燃料削減、排出規制対応をスマートに。"
          : i18n.language?.startsWith("zh")
          ? "由 ESA 支持的项目，将卫星对地观测、GNSS 与卫星通信整合至 iHelm 平台，实现更智能的航行规划、燃油节约与排放合规。"
          : "An ESA-backed project integrating satellite Earth Observation, GNSS and satellite communication with Cetasol's iHelm platform for smarter voyage planning, fuel savings and emissions compliance.",
      href: "/technology",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://cetasol.com/projects" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/projects" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Projects", "item": "https://cetasol.com/projects" },
          ],
        })}</script>
      </Helmet>
      <Header />
      <main id="main-content">
        <section className="relative pt-32 pb-12">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">
                Resources
              </p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                {heading}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">{intro}</p>
            </motion.div>
          </div>
        </section>

        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl divide-y divide-border border-t border-border">
              {projects.map((project, i) => (
                <motion.article
                  key={project.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="py-10 grid md:grid-cols-[auto,1fr] gap-6"
                >
                  <div className="flex md:block">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <project.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                      {project.name}
                    </h2>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                      {project.funder}
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-5">
                      {project.summary}
                    </p>
                    <Link
                      to={project.href}
                      className="inline-flex items-center gap-2 text-primary hover:text-primary-hover font-medium"
                    >
                      {readMore}
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Projects;
